import React, { useState, useEffect, lazy, Suspense } from 'react';
import PhoneField from './PhoneField';
import ConfirmDialog from './ConfirmDialog';
import {
  Service,
  BusinessHours,
  TenantPhoto,
  fetchAdminServices,
  createAdminService,
  updateAdminService,
  deleteAdminService,
  fetchBusinessHours,
  updateBusinessHours,
  fetchAdminProfile,
  updateAdminProfile,
  fetchAdminPhotos,
  uploadAdminPhoto,
  deleteAdminPhoto,
  reorderAdminPhotos,
} from '../api';
import {
  Plus,
  Trash2,
  Check,
  X,
  Pencil,
  Clock,
  DollarSign,
  Loader2,
  Phone,
  Instagram,
  Upload,
  ArrowUp,
  ArrowDown,
  ImageIcon,
} from 'lucide-react';
import { TIME_OPTIONS, formatTime12 } from '../lib/time';
const LocationPicker = lazy(() => import('./LocationPicker'));

interface AdminSalonSettingsProps {
  onServicesChanged: () => void;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DAYS_SHORT: Record<string, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

export default function AdminSalonSettings({ onServicesChanged }: AdminSalonSettingsProps) {
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', price: '', duration: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', price: '', duration: '60' });
  const [savingService, setSavingService] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState<string | undefined>('');
  const [isWhatsapp, setIsWhatsapp] = useState(false);
  const [instagramHandle, setInstagramHandle] = useState('');
  const [loadingPhone, setLoadingPhone] = useState(true);
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneDirty, setPhoneDirty] = useState(false);
  const [phoneSavedMessage, setPhoneSavedMessage] = useState('');

  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [locationDirty, setLocationDirty] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationSavedMessage, setLocationSavedMessage] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; name: string } | null>(null);

  const [photos, setPhotos] = useState<TenantPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoOrderDirty, setPhotoOrderDirty] = useState(false);
  const [savingPhotoOrder, setSavingPhotoOrder] = useState(false);
  const [photoSavedMessage, setPhotoSavedMessage] = useState('');
  const [deletePhotoConfirm, setDeletePhotoConfirm] = useState<{ id: number } | null>(null);
  const photoInputRef = React.useRef<HTMLInputElement>(null);

  const [hours, setHours] = useState<BusinessHours>({});
  const [loadingHours, setLoadingHours] = useState(true);
  const [savingHours, setSavingHours] = useState(false);
  const [hoursDirty, setHoursDirty] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');

  // A load that is still in flight when this effect re-runs (React remounts the
  // panel, and StrictMode double-invokes the mount effect in dev) must not apply
  // its result: it would clear the spinner over half-loaded state, and overwrite
  // whatever the owner has typed since. Each loader takes a liveness check so a
  // superseded run drops its response instead.
  useEffect(() => {
    let current = true;
    const isCurrent = () => current;
    loadProfile(isCurrent);
    loadServices(isCurrent);
    loadHours(isCurrent);
    loadPhotos(isCurrent);
    return () => {
      current = false;
    };
  }, []);

  const loadProfile = async (isCurrent: () => boolean = () => true) => {
    setLoadingPhone(true);
    try {
      const data = await fetchAdminProfile();
      if (!isCurrent()) return;
      setPhoneNumber(data.phoneNumber || '');
      setIsWhatsapp(data.isWhatsapp || false);
      setInstagramHandle(data.instagramHandle || '');
      setAddress(data.address || '');
      setLatitude(data.latitude ?? null);
      setLongitude(data.longitude ?? null);
    } catch {
      /* ignore */
    }
    if (!isCurrent()) return;
    setLoadingPhone(false);
  };

  const loadPhotos = async (isCurrent: () => boolean = () => true) => {
    setLoadingPhotos(true);
    try {
      const data = await fetchAdminPhotos();
      if (!isCurrent()) return;
      setPhotos(data);
    } catch {
      /* ignore */
    }
    if (!isCurrent()) return;
    setLoadingPhotos(false);
  };

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      await uploadAdminPhoto(file);
      await loadPhotos();
    } catch {
      /* ignore */
    }
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleDeletePhoto = async (id: number) => {
    try {
      await deleteAdminPhoto(id);
      await loadPhotos();
    } catch {
      /* ignore */
    }
    setDeletePhotoConfirm(null);
  };

  const movePhoto = (index: number, direction: -1 | 1) => {
    const newPhotos = [...photos];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newPhotos.length) return;
    [newPhotos[index], newPhotos[targetIndex]] = [newPhotos[targetIndex], newPhotos[index]];
    setPhotos(newPhotos);
    setPhotoOrderDirty(true);
  };

  const handleSavePhotoOrder = async () => {
    setSavingPhotoOrder(true);
    try {
      await reorderAdminPhotos(photos.map((p) => p.id));
      setPhotoOrderDirty(false);
      setPhotoSavedMessage('Saved');
      setTimeout(() => setPhotoSavedMessage(''), 2500);
    } catch {
      /* ignore */
    }
    setSavingPhotoOrder(false);
  };

  const handleSavePhone = async () => {
    setSavingPhone(true);
    try {
      await updateAdminProfile({ phoneNumber: phoneNumber || '', isWhatsapp, instagramHandle });
      setPhoneDirty(false);
      setPhoneSavedMessage('Saved');
      setTimeout(() => setPhoneSavedMessage(''), 2500);
    } catch {
      /* ignore */
    }
    setSavingPhone(false);
  };

  const handleSaveLocation = async () => {
    setSavingLocation(true);
    try {
      await updateAdminProfile({
        address,
        latitude: latitude !== null ? parseFloat(Number(latitude).toFixed(6)) : null,
        longitude: longitude !== null ? parseFloat(Number(longitude).toFixed(6)) : null,
      });
      setLocationDirty(false);
      setLocationSavedMessage('Saved');
      setTimeout(() => setLocationSavedMessage(''), 2500);
    } catch {
      /* ignore */
    }
    setSavingLocation(false);
  };

  const loadServices = async (isCurrent: () => boolean = () => true) => {
    setLoadingServices(true);
    try {
      const data = await fetchAdminServices();
      if (!isCurrent()) return;
      setServices(data);
    } catch {
      /* ignore */
    }
    if (!isCurrent()) return;
    setLoadingServices(false);
  };

  const loadHours = async (isCurrent: () => boolean = () => true) => {
    setLoadingHours(true);
    try {
      const data = await fetchBusinessHours();
      if (!isCurrent()) return;
      setHours(data.businessHours);
    } catch {
      /* ignore */
    }
    if (!isCurrent()) return;
    setLoadingHours(false);
  };

  const handleAddService = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingService(true);
    try {
      await createAdminService({
        name: addForm.name,
        price: parseFloat(addForm.price),
        duration: parseInt(addForm.duration),
      });
      setAddForm({ name: '', price: '', duration: '60' });
      setShowAddForm(false);
      await loadServices();
      onServicesChanged();
    } catch {
      /* ignore */
    }
    setSavingService(false);
  };

  const handleStartEdit = (service: Service) => {
    setEditingId(service.id);
    setEditForm({
      name: service.name,
      price: String(service.price),
      duration: String(service.duration),
    });
  };

  const handleSaveEdit = async () => {
    if (editingId === null) return;
    setSavingService(true);
    try {
      await updateAdminService(editingId, {
        name: editForm.name,
        price: parseFloat(editForm.price),
        duration: parseInt(editForm.duration),
      });
      setEditingId(null);
      await loadServices();
      onServicesChanged();
    } catch {
      /* ignore */
    }
    setSavingService(false);
  };

  const handleDeleteService = async (id: number) => {
    try {
      await deleteAdminService(id);
      await loadServices();
      onServicesChanged();
    } catch {
      /* ignore */
    }
    setDeleteConfirm(null);
  };

  const toggleDay = (day: string) => {
    setHours((prev) => ({
      ...prev,
      [day]: prev[day] ? null : { open: '09:00', close: '18:00' },
    }));
    setHoursDirty(true);
  };

  const updateDayTime = (day: string, field: 'open' | 'close', value: string) => {
    setHours((prev) => ({
      ...prev,
      [day]: { ...(prev[day] as { open: string; close: string }), [field]: value },
    }));
    setHoursDirty(true);
  };

  const handleSaveHours = async () => {
    setSavingHours(true);
    try {
      await updateBusinessHours(hours);
      setHoursDirty(false);
      setSavedMessage('Saved');
      setTimeout(() => setSavedMessage(''), 2500);
    } catch {
      /* ignore */
    }
    setSavingHours(false);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* ── Photos ── */}
      <div className="border-default bg-surface overflow-visible rounded-xl border shadow-sm">
        <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Photos</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              Showcase your salon with up to 6 photos
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {photoSavedMessage && (
              <span className="text-success flex items-center gap-1 text-xs font-medium sm:text-sm">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {photoSavedMessage}
              </span>
            )}
            {photoOrderDirty && (
              <button
                onClick={handleSavePhotoOrder}
                disabled={savingPhotoOrder}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors disabled:opacity-50 sm:gap-2 sm:px-4 sm:text-sm"
              >
                {savingPhotoOrder ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save Order
              </button>
            )}
          </div>
        </div>
        <div className="px-4 py-4 sm:px-6">
          {loadingPhotos ? (
            <div className="text-fg-subtle py-4 text-center">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
              Loading photos...
            </div>
          ) : (
            <>
              {photos.length === 0 ? (
                <div className="text-fg-subtle py-8 text-center">
                  <ImageIcon className="mx-auto mb-2 h-8 w-8" />
                  <p className="text-sm">No photos yet. Add photos to showcase your salon.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {photos.map((photo, index) => (
                    <div
                      key={photo.id}
                      className="group relative aspect-square overflow-hidden rounded-lg"
                    >
                      <img
                        src={photo.image}
                        alt={`Salon photo ${index + 1}`}
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/30" />
                      <div className="absolute top-1.5 right-1.5 flex gap-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                        <button
                          onClick={() => movePhoto(index, -1)}
                          disabled={index === 0}
                          className="text-fg-secondary hover:bg-surface bg-surface/90 rounded-md p-1 shadow transition-colors disabled:opacity-30"
                          title="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => movePhoto(index, 1)}
                          disabled={index === photos.length - 1}
                          className="text-fg-secondary hover:bg-surface bg-surface/90 rounded-md p-1 shadow transition-colors disabled:opacity-30"
                          title="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setDeletePhotoConfirm({ id: photo.id })}
                          className="hover:bg-surface bg-surface/90 text-danger rounded-md p-1 shadow transition-colors"
                          title="Delete photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {photos.length < 6 && (
                <div className="mt-3">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleUploadPhoto}
                    className="hidden"
                  />
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="hover:text-accent border-default text-fg-muted hover:border-strong flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-3 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Phone Number ── */}
      <div className="border-default bg-surface overflow-visible rounded-xl border shadow-sm">
        <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Contact Phone</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              Displayed on your shop page so customers can reach you
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {phoneSavedMessage && (
              <span className="text-success flex items-center gap-1 text-xs font-medium sm:text-sm">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {phoneSavedMessage}
              </span>
            )}
            {phoneDirty && (
              <button
                onClick={handleSavePhone}
                disabled={savingPhone}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors disabled:opacity-50 sm:gap-2 sm:px-4 sm:text-sm"
              >
                {savingPhone ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save
              </button>
            )}
          </div>
        </div>
        <div className="px-4 py-4 sm:px-6">
          {loadingPhone ? (
            <div className="text-fg-subtle py-4 text-center">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
              Loading...
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Phone className="text-fg-subtle h-5 w-5 shrink-0" />
                <PhoneField
                  value={phoneNumber}
                  onChange={(val) => {
                    setPhoneNumber(val);
                    setPhoneDirty(true);
                  }}
                  selectClassName="px-2 py-2 border border-default rounded-lg text-sm focus:ring-2 focus:ring-brand-600 focus:border-transparent outline-none bg-surface"
                  inputClassName="flex-1 px-3 py-2 border border-default rounded-lg text-sm focus:ring-2 focus:ring-brand-600 focus:border-transparent outline-none"
                />
              </div>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={isWhatsapp}
                  onChange={(e) => {
                    setIsWhatsapp(e.target.checked);
                    setPhoneDirty(true);
                  }}
                  className="border-strong text-success focus:ring-success h-4 w-4 rounded"
                />
                <svg className="text-success h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <span className="text-fg-secondary text-sm">This is a WhatsApp number</span>
              </label>
              <div className="flex items-center gap-3">
                <Instagram className="text-fg-subtle h-5 w-5 shrink-0" />
                <div className="focus-within:ring-brand-600 border-default bg-surface flex flex-1 items-center gap-0 rounded-lg border text-sm focus-within:border-transparent focus-within:ring-2">
                  <span className="text-fg-subtle pl-3">@</span>
                  <input
                    type="text"
                    value={instagramHandle}
                    onChange={(e) => {
                      setInstagramHandle(e.target.value.replace(/^@/, ''));
                      setPhoneDirty(true);
                    }}
                    className="flex-1 rounded-r-lg border-none px-1 py-2 text-sm outline-none"
                    placeholder="your.handle"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Shop Location ── */}
      <div className="border-default bg-surface overflow-visible rounded-xl border shadow-sm">
        <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Shop Location</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              Set your shop's map location for customers
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {locationSavedMessage && (
              <span className="text-success flex items-center gap-1 text-xs font-medium sm:text-sm">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {locationSavedMessage}
              </span>
            )}
            {locationDirty && (
              <button
                onClick={handleSaveLocation}
                disabled={savingLocation}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors disabled:opacity-50 sm:gap-2 sm:px-4 sm:text-sm"
              >
                {savingLocation ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save
              </button>
            )}
          </div>
        </div>
        <div className="px-4 py-4 sm:px-6">
          <div className="mb-4">
            <label className="text-fg-secondary mb-1.5 block text-sm font-medium">
              Street Address
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                setLocationDirty(true);
              }}
              placeholder="e.g. 123 Main St, City"
              className="focus:border-brand-600 focus:ring-brand-600 border-strong w-full rounded-lg border px-3 py-2 text-sm shadow-sm transition-colors focus:ring-1 focus:outline-none"
            />
          </div>
          <Suspense
            fallback={
              <div className="text-fg-subtle py-8 text-center text-sm">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                Loading map...
              </div>
            }
          >
            <LocationPicker
              latitude={latitude}
              longitude={longitude}
              onChange={(lat: number, lng: number) => {
                setLatitude(lat);
                setLongitude(lng);
                setLocationDirty(true);
              }}
            />
          </Suspense>
        </div>
      </div>

      {/* ── Services ── */}
      <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
        <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Services</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              Manage what you offer, pricing, and durations
            </p>
          </div>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-brand-600 hover:bg-brand-700 flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors sm:gap-2 sm:px-4 sm:text-sm"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add Service</span>
              <span className="sm:hidden">Add</span>
            </button>
          )}
        </div>

        <div className="divide-subtle divide-y">
          {/* Add form */}
          {showAddForm && (
            <form onSubmit={handleAddService} className="bg-canvas/40 px-4 py-4 sm:px-6">
              <div className="space-y-3 sm:grid sm:grid-cols-[1fr_100px_100px_auto] sm:items-end sm:gap-3 sm:space-y-0">
                <div>
                  <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                    Service Name
                  </label>
                  <input
                    type="text"
                    required
                    value={addForm.name}
                    onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))}
                    className="focus:ring-brand-600 border-default bg-surface w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                    placeholder="Service name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:contents">
                  <div>
                    <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                      Price ($)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={addForm.price}
                      onChange={(e) => setAddForm((p) => ({ ...p, price: e.target.value }))}
                      className="focus:ring-brand-600 border-default bg-surface w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                      placeholder="Price"
                    />
                  </div>
                  <div>
                    <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                      Duration
                    </label>
                    <select
                      value={addForm.duration}
                      onChange={(e) => setAddForm((p) => ({ ...p, duration: e.target.value }))}
                      className="focus:ring-brand-600 border-default bg-surface w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                    >
                      {Array.from({ length: 36 }, (_, i) => (i + 1) * 5).map((m) => (
                        <option key={m} value={m}>
                          {m} min
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-2">
                  <button
                    type="submit"
                    disabled={savingService}
                    aria-label="Add service"
                    className="bg-brand-600 hover:bg-brand-700 flex-1 rounded-lg p-2 text-white transition-colors disabled:opacity-50 sm:flex-none"
                  >
                    <Check className="mx-auto h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="bg-muted text-fg-secondary hover:bg-muted-strong flex-1 rounded-lg p-2 transition-colors sm:flex-none"
                  >
                    <X className="mx-auto h-4 w-4" />
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* List */}
          {loadingServices ? (
            <div className="text-fg-subtle px-4 py-12 text-center sm:px-6">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
              Loading services...
            </div>
          ) : services.length === 0 ? (
            <div className="text-fg-subtle px-4 py-12 text-center sm:px-6">
              No services yet. Add your first service to get started.
            </div>
          ) : (
            services.map((service) => (
              <div
                key={service.id}
                className="group hover:bg-canvas/50 px-4 py-3 transition-colors sm:px-6 sm:py-4"
              >
                {editingId === service.id ? (
                  <div className="space-y-3 sm:grid sm:grid-cols-[1fr_100px_100px_auto] sm:items-center sm:gap-3 sm:space-y-0">
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2"
                    />
                    <div className="grid grid-cols-2 gap-3 sm:contents">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm((p) => ({ ...p, price: e.target.value }))}
                        className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2"
                      />
                      <select
                        value={editForm.duration}
                        onChange={(e) => setEditForm((p) => ({ ...p, duration: e.target.value }))}
                        className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2"
                      >
                        {Array.from({ length: 36 }, (_, i) => (i + 1) * 5).map((m) => (
                          <option key={m} value={m}>
                            {m} min
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={handleSaveEdit}
                        disabled={savingService}
                        className="bg-brand-600 hover:bg-brand-700 flex-1 rounded-md p-1.5 text-white transition-colors disabled:opacity-50 sm:flex-none"
                      >
                        <Check className="mx-auto h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="bg-muted text-fg-secondary hover:bg-muted-strong flex-1 rounded-md p-1.5 transition-colors sm:flex-none"
                      >
                        <X className="mx-auto h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-fg truncate text-sm font-medium sm:text-base">
                        {service.name}
                      </div>
                      {/* Mobile: show price & duration inline below name */}
                      <div className="text-fg-muted mt-0.5 flex items-center gap-3 text-xs sm:hidden">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {service.duration}m
                        </span>
                        <span className="text-fg font-semibold">
                          ${Number(service.price).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    {/* Desktop: price & duration */}
                    <div className="text-fg-secondary hidden items-center gap-1 text-sm tabular-nums sm:flex">
                      <Clock className="text-fg-subtle h-3.5 w-3.5" />
                      {service.duration} min
                    </div>
                    <div className="text-fg hidden w-20 items-center justify-end gap-1 text-sm font-semibold tabular-nums sm:flex">
                      <DollarSign className="text-fg-subtle h-3.5 w-3.5" />
                      {Number(service.price).toFixed(2)}
                    </div>
                    {/* Actions: always visible on mobile, hover on desktop */}
                    <div className="flex gap-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                      <button
                        onClick={() => handleStartEdit(service)}
                        className="hover:text-accent text-fg-subtle hover:bg-canvas rounded-md p-1.5 transition-colors"
                        title="Edit service"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ id: service.id, name: service.name })}
                        className="text-fg-subtle hover:bg-danger-soft hover:text-danger rounded-md p-1.5 transition-colors"
                        title="Delete service"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Business Hours ── */}
      <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
        <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Business Hours</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              Set your opening and closing times for each day
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {savedMessage && (
              <span className="text-success flex items-center gap-1 text-xs font-medium sm:text-sm">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {savedMessage}
              </span>
            )}
            {hoursDirty && (
              <button
                onClick={handleSaveHours}
                disabled={savingHours}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors disabled:opacity-50 sm:gap-2 sm:px-4 sm:text-sm"
              >
                {savingHours ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save
              </button>
            )}
          </div>
        </div>

        {loadingHours ? (
          <div className="text-fg-subtle px-4 py-12 text-center sm:px-6">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Loading hours...
          </div>
        ) : (
          <div className="divide-subtle divide-y">
            {DAYS.map((day) => {
              const isOpen = hours[day] !== null && hours[day] !== undefined;
              const dayHours = hours[day];

              return (
                <div key={day} className="px-4 py-3 sm:px-6 sm:py-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Day name */}
                    <div className="text-fg w-10 text-sm font-medium sm:w-28 sm:text-base">
                      <span className="sm:hidden">{DAYS_SHORT[day]}</span>
                      <span className="hidden sm:inline">{day}</span>
                    </div>

                    {/* Toggle */}
                    <button
                      onClick={() => toggleDay(day)}
                      className={`relative h-5 w-10 shrink-0 rounded-full transition-colors sm:h-6 sm:w-11 ${
                        isOpen ? 'bg-brand-600' : 'bg-muted-strong'
                      }`}
                    >
                      <span
                        className={`bg-surface absolute top-0.5 left-0.5 h-4 w-4 rounded-full shadow transition-transform sm:h-5 sm:w-5 ${
                          isOpen ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>

                    {isOpen && dayHours ? (
                      <div className="flex min-w-0 items-center gap-1.5 text-sm sm:gap-2">
                        <select
                          value={dayHours.open}
                          onChange={(e) => updateDayTime(day, 'open', e.target.value)}
                          className="focus:ring-brand-600 border-default bg-surface rounded-lg border px-1.5 py-1 text-xs outline-none focus:border-transparent focus:ring-2 sm:px-2 sm:py-1.5 sm:text-sm"
                        >
                          {TIME_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {formatTime12(t)}
                            </option>
                          ))}
                        </select>
                        <span className="text-fg-subtle text-xs sm:text-sm">-</span>
                        <select
                          value={dayHours.close}
                          onChange={(e) => updateDayTime(day, 'close', e.target.value)}
                          className="focus:ring-brand-600 border-default bg-surface rounded-lg border px-1.5 py-1 text-xs outline-none focus:border-transparent focus:ring-2 sm:px-2 sm:py-1.5 sm:text-sm"
                        >
                          {TIME_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {formatTime12(t)}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <span className="text-fg-subtle text-xs italic sm:text-sm">Closed</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Service"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteConfirm && handleDeleteService(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm(null)}
      />
      <ConfirmDialog
        open={deletePhotoConfirm !== null}
        title="Delete Photo"
        message="Are you sure you want to delete this photo? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deletePhotoConfirm && handleDeletePhoto(deletePhotoConfirm.id)}
        onCancel={() => setDeletePhotoConfirm(null)}
      />
    </div>
  );
}
